import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { MilestoneMessage } from '../types';
import { MessageAttachmentPicker } from './MessageAttachmentPicker';
import { MessageAttachments } from './MessageAttachments';

export type CaseComposer = {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled: boolean;
  sendDisabled: boolean;
  sending?: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  /** Overrides for the attestation view; the objection case keeps the defaults. */
  label?: string;
  placeholder?: string;
  submitLabel?: string;
  /** Shown under the send row, e.g. that a message does not approve the stage. */
  hint?: string;
  /** Files to send with the message (ClickUp 869fj6n1d); no picker without it. */
  files?: File[];
  onFilesChange?: (files: File[]) => void;
  /** "Be om attest" for the company while the stage awaits the customer (ClickUp 869fm3fy8). */
  requestAttestation?: { checked: boolean; onChange: (checked: boolean) => void; label: string; hint: string };
  /** Why another reminder cannot be sent yet, shown instead of the option (ClickUp 869fmfkyz). */
  requestAttestationBlocked?: string;
};

export const isOwnMessage = (message: MilestoneMessage, userRole: 'company' | 'person') =>
  userRole === 'person' ? message.userRole === 'person' : ['company', 'builder'].includes(message.userRole);

export const isSystemEvent = (message: MilestoneMessage) =>
  message.userRole === 'system' || (Boolean(message.draftAction) && !message.attestationAction);

export const formatCaseDate = (iso: string | null | undefined, withTime = false) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('sv-SE', withTime
    ? { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { month: 'short', day: 'numeric' });
};

interface CaseThreadProps {
  messages: MilestoneMessage[];
  userRole: 'company' | 'person';
  composer: CaseComposer;
  onReferenceMessage?: (message: MilestoneMessage) => void;
  referenceBusy?: boolean;
  error?: string | null;
  /**
   * Opened from a notification or e-mail (ClickUp 869fnf0e5): when the other
   * party's message is the latest, scroll it into view once, so it is read
   * next to the attestation without searching for it.
   */
  revealLatest?: boolean;
}

/**
 * The stage's dialogue in the same design as Meddelanden: oldest at the top,
 * opened at the newest, with the composer underneath. Shared by the objection
 * case and attestation, so both modals look the same (ClickUp 869fkhmbb).
 */
export default function CaseThread({ messages, userRole, composer, onReferenceMessage, referenceBusy = false, error, revealLatest = false }: CaseThreadProps) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLOListElement>(null);
  const revealedRef = useRef(false);
  const counterpartRole = userRole === 'person' ? 'company' : 'person';
  const counterpartName = [...messages].reverse().find(message => !isSystemEvent(message) && !isOwnMessage(message, userRole))?.userName;
  const counterpartLabel = t(`objectionCase.counterpart.${counterpartRole}`);
  const latestCounterpartMessage = [...messages].reverse().find(message =>
    !isSystemEvent(message) && !message.attestationAction && !isOwnMessage(message, userRole));

  // Oldest at the top and newest at the bottom, opened at the newest message,
  // as on Meddelanden (MessageConversation).
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages.length]);

  const latestMessage = [...messages].reverse().find(message => !isSystemEvent(message));
  const revealTarget = revealLatest && latestCounterpartMessage && latestMessage?.id === latestCounterpartMessage.id
    ? latestCounterpartMessage.id
    : null;
  useEffect(() => {
    if (!revealTarget || revealedRef.current) return;
    const bubble = listRef.current?.querySelector('[data-testid="counterparty-message-notice"]');
    if (!bubble) return;
    revealedRef.current = true;
    bubble.scrollIntoView?.({ block: 'nearest' });
  }, [revealTarget]);

  return (
    <section className="objection-thread" aria-labelledby="objection-thread-title">
      <h3 id="objection-thread-title" className="objection-section-title">
        {counterpartName
          ? t('objectionCase.thread.titleWithName', { name: counterpartName, role: counterpartLabel })
          : t('objectionCase.thread.title', { role: counterpartLabel })}
      </h3>
      <div className="objection-thread-box">
        <ol ref={listRef} className="message-conversation-list objection-conversation" aria-label={t('messageThreads.conversation')}
          data-testid="milestone-message-list">
          {messages.length === 0 && <li className="messages-empty">{t('objectionCase.thread.empty')}</li>}
          {messages.map(message => {
            if (isSystemEvent(message)) {
              return (
                <li key={message.id} className="message-subject-line objection-event" data-testid="objection-thread-event">
                  <i className="feather-file-text" aria-hidden="true"></i>
                  <span>{message.message}</span>
                  <time dateTime={message.createdAt}>{formatCaseDate(message.createdAt, true)}</time>
                </li>
              );
            }
            const own = isOwnMessage(message, userRole);
            const latestFromCounterpart = message.id === latestCounterpartMessage?.id;
            return (
              <li key={message.id} className={`message-bubble${own ? ' is-mine' : ''}`}
                data-testid={latestFromCounterpart ? 'counterparty-message-notice' : 'objection-thread-message'}>
                <div className="message-bubble-meta">
                  <strong>{own ? t('objectionCase.thread.you') : message.userName}</strong>
                  <time dateTime={message.createdAt}>· {formatCaseDate(message.createdAt, true)}</time>
                  {message.attestationAction === 'rejected' && (
                    <span className="message-tag is-wait">{t('objectionCase.thread.objectionTag')}</span>
                  )}
                  {message.attestationAction === 'approved' && (
                    <span className="message-tag">{t('objectionCase.thread.approvalTag')}</span>
                  )}
                </div>
                {message.message && <p className="message-bubble-text">{message.message}</p>}
                <MessageAttachments attachments={message.attachments} />
                {latestFromCounterpart && (
                  <div className="objection-bubble-actions">
                    <button type="button" className="message-reply-to" onClick={() => composer.inputRef.current?.focus()}>
                      {t('milestoneAttestation.replyNow')}
                    </button>
                    {onReferenceMessage && (
                      <button type="button" className="message-reply-to" onClick={() => onReferenceMessage(message)}
                        disabled={referenceBusy} data-testid="create-variation-with-reference">
                        {t(referenceBusy ? 'conflictVariation.opening' : 'conflictVariation.createWithReference')}
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
        <form className="message-compose objection-compose" onSubmit={event => { event.preventDefault(); if (!composer.sendDisabled) composer.onSend(); }}>
          {error && <div className="alert alert-danger mb-0" role="alert">{error}</div>}
          <label htmlFor="milestone-reply-message" className="form-label fw-semibold mb-0">
            {composer.label ?? t(userRole === 'person' ? 'objectionCase.thread.composeToCompany' : 'objectionCase.thread.composeToPerson')}
          </label>
          <textarea
            ref={composer.inputRef}
            id="milestone-reply-message"
            className="form-control"
            rows={2}
            placeholder={composer.placeholder ?? t(userRole === 'person' ? 'objectionCase.thread.placeholderPerson' : 'objectionCase.thread.placeholderCompany')}
            value={composer.value}
            onChange={event => composer.onChange(event.target.value)}
            disabled={composer.disabled}
            data-testid="conflict-reply-input"
          />
          {composer.requestAttestationBlocked && (
            <p className="message-attestation-request is-blocked" data-testid="case-request-attestation-blocked">
              <i className="feather-clock" aria-hidden="true"></i>
              <span>{composer.requestAttestationBlocked}</span>
            </p>
          )}
          {composer.requestAttestation && (
            <label className="message-attestation-request" data-testid="case-request-attestation">
              <input type="checkbox" className="form-check-input" checked={composer.requestAttestation.checked} disabled={composer.disabled}
                onChange={event => composer.requestAttestation!.onChange(event.target.checked)} />
              <span>
                <strong>{composer.requestAttestation.label}</strong>
                <small>{composer.requestAttestation.hint}</small>
              </span>
            </label>
          )}
          {composer.onFilesChange && (
            <MessageAttachmentPicker files={composer.files ?? []} onChange={composer.onFilesChange} disabled={composer.disabled} />
          )}
          <div className="message-compose-row">
            {composer.hint ? <small className="text-muted">{composer.hint}</small> : <span />}
            <button type="submit" className="btn btn-primary" disabled={composer.sendDisabled} data-testid="conflict-reply-submit">
              {composer.sending ? t('messageThreads.sending') : composer.submitLabel ?? t('messageThreads.send')}
            </button>
          </div>
          <small className="text-muted">{t('objectionCase.thread.saved')}</small>
        </form>
      </div>
    </section>
  );
}
